import React from 'react';
import ProgressiveImage from 'react-progressive-image';
import ReactImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import Pannellum, { PannellumRef } from 'rc-pannellum';
import $ from 'jquery';
import Request from '../utils/Request';
import { Work } from '../types/WorkType';
import LoadingComponent from '../components/LoadingComponent';
import Photos from '../layouts/Photos';
import { Col, Row, Stack } from 'react-bootstrap';

type HomePageState = {
  currentImage: number;
  viewerIsOpen: boolean;
  photos: Work[];
  page: number;
  isLoad: boolean;
  isLastItem: boolean;
  loadedPhotos: number[];
  loadedPages: number[];
  isComplete: boolean;
  canSwipe?: boolean;
  headerState: boolean;
};

type Props = { category: 1 | 2 | 3; loading?: React.RefObject<LoadingComponent | null> };

export default class HomePage extends React.Component<Props, HomePageState> {
  private countPerPage = 7;
  private viewerRef: React.RefObject<PannellumRef>;
  private reactImageGallery: React.RefObject<ReactImageGallery>;
  private lastListener: (() => void) | undefined = undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      currentImage: 0,
      page: 1,
      viewerIsOpen: false,
      isLoad: false,
      isLastItem: false,
      photos: [],
      loadedPhotos: [],
      loadedPages: [],
      headerState: false,
      isComplete: false
    };
    this.viewerRef = React.createRef<PannellumRef>();
    this.reactImageGallery = React.createRef<ReactImageGallery>();
  }

  componentDidMount(): void {
    document.title = process.env.REACT_APP_NAME ?? 'Kaz Studio';

    this.loadProjects();

    if (!this.viewerRef) return;
    const viewer = this.viewerRef.current?.getViewer();
    if (viewer) {
      viewer.on('mousedown', (e: MouseEvent) => {
        console.log(e);
      });
    }
  }

  loadProjects() {
    const { category } = this.props;
    this.setState({ photos: [] });
    Request.sendRequest(`works?count=${this.countPerPage}&category=${category}`).then((x) => {
      const result = x.data;
      if (result.success) {
        this.addPhotos(result.data);
      }

      this.lastListener = () => this.loadMore();
      $(window).on('scroll', this.lastListener);
      this.props.loading?.current?.closeAnimation();
    });
  }

  loadMore() {
    const hDocument = $(document).height() ?? 0;
    const hWindow = $(window).height() ?? 0;
    const sWindow = Number.parseInt(($(window).scrollTop() ?? 0).toString());
    const diffs = hDocument - hWindow - 100;

    if (sWindow > diffs && !this.state.isLastItem && !this.state.isLoad) {
      this.loadNextPage();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.category === this.props.category) return;
    this.loadProjects();
  }

  componentWillUnmount() {
    $(window).off('scroll', this.lastListener);
  }

  loadNextPage(): void {
    const currentPage = this.state.page + 1;
    if (this.state.isLoad || currentPage == 1 || currentPage in this.state.loadedPages) return;
    this.setState({
      page: this.state.page + 1,
      loadedPages: [...this.state.loadedPages, currentPage]
    });
    this.setState({ isLoad: true });
    Request.sendRequest(
      `works?category=${this.props.category}&count=${this.countPerPage}&page=${this.state.page}`
    ).then((x) => {
      const result = x.data;
      if (result.success && result.data.length) {
        this.addPhotos(result.data);
        this.setState({ isComplete: this.state.photos.length >= result.count });
      } else if (result.data.length === 0) {
        this.setState({ isLastItem: true });
      }
      this.setState({ isLoad: false });
    });
  }

  addPhotos(newPhotos: Work[]): void {
    this.setState({ photos: [...this.state.photos, ...newPhotos] });
  }

  getImageItemGallery = (item: ReactImageGalleryItem): JSX.Element => {
    const data = this.state.photos.filter((photo) => photo.gallery.url == item.original)[0];
    const url: string = process.env.REACT_APP_API_URL ?? '';
    let title = data.title || '';
    if (data.details) title += (data.title != '' ? ' / ' : '') + data.details;
    switch (data.category.id) {
      case 1:
        return (
          <div className="photo-open">
            <Row>
              <ProgressiveImage src={item.original} placeholder={item.thumbnail ?? ''}>
                {(src: string) => (
                  <Col>
                    <img
                      className="image-gallery-image col align-self-center"
                      src={src}
                      alt="an image"
                    />
                  </Col>
                )}
              </ProgressiveImage>
            </Row>
            <Row>
              <Col className="align-self-center text">{title}</Col>
            </Row>
          </div>
        );
      case 3:
        return (
          <Stack gap={3}>
            <Pannellum
              ref={this.viewerRef}
              className="viewer gallery-item"
              autoLoad={true}
              panorama={`${url}works/${data.id}/file`}
              showControls={false}
            />
            <Col className="align-self-center text">{title}</Col>
          </Stack>
        );
      default:
        return <></>;
    }
  };

  closeViewer() {
    $('body').removeClass('overflow-hidden');
    if (this.state.headerState) {
      $('.header').css('top', 0);
    }
    this.setState({ viewerIsOpen: false, headerState: false });
  }

  getColumnsCount = (width: number): 2 | 1 => (width > 500 ? 2 : 1);

  changePage = (delta: number) => {
    const index = (this.reactImageGallery.current?.getCurrentIndex() ?? 0) + delta;
    this.reactImageGallery.current?.slideToIndex(index);
  };

  render(): JSX.Element {
    const openLightBox = (index: number) => {
      $('body').addClass('overflow-hidden');
      if ($('.header').css('top') === '0px') {
        $('.header').css('top', -180);
        this.setState({ headerState: true });
      }
      this.setState({
        currentImage: index,
        viewerIsOpen: true,
        loadedPhotos: [...this.state.loadedPhotos, index]
      });
    };

    const works = this.state.photos.flat();

    const images = works.map((x) => {
      const item: ReactImageGalleryItem = {
        thumbnail: x.thumbnails,
        original: x.gallery.url,
        originalHeight: x.gallery.size.height,
        originalWidth: x.gallery.size.width
      };
      return item;
    });

    const onItemClickGallery = (index: number) => {
      images[index].original = works[index].gallery.url;
      openLightBox(index);
    };
    return (
      <div className="home" key={'home-' + this.props.category}>
        <div>
          <Photos onClick={onItemClickGallery} photos={this.state.photos} />
        </div>
        {this.state.viewerIsOpen ? (
          <>
            <div className="sliders p-3 ">
              <div className="background" onClick={() => this.closeViewer()}></div>
              <span
                onClick={() => this.changePage(-1)}
                className="image-gallery-icon image-gallery-left-nav nav left"
              >
                Previous
              </span>
              <span
                onClick={() => this.changePage(1)}
                className={'image-gallery-icon image-gallery-right-nav nav right'}
              >
                Next
              </span>
              <ReactImageGallery
                onClick={() => (this.props.category === 1 ? this.closeViewer() : undefined)}
                items={images}
                additionalClass="mx-auto d-flex"
                ref={this.reactImageGallery}
                lazyLoad={true}
                disableSwipe={this.props.category !== 1}
                onBeforeSlide={(index): void => {
                  images[index].original = works[index].gallery.url;
                  this.setState({ loadedPhotos: [...this.state.loadedPhotos, index] });
                  return;
                }}
                renderItem={this.getImageItemGallery}
                showNav={false}
                showThumbnails={false}
                startIndex={this.state.currentImage}
                showPlayButton={false}
                showFullscreenButton={false}
              />
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  }
}
